import { RefObject, useEffect, useRef, useState } from 'react'
import { HeroTextState, heroTextStateAt, HERO_LOOP_MS } from './timeline'

type Args = {
  stageRef: RefObject<HTMLDivElement | null>
  enabled: boolean
  codeLength: number
}

// Reads the CSS loop's own clock rather than keeping a JS timer, so the typed
// characters and the counting total can never drift from the choreography —
// including after the animation is paused off-screen or the tab is throttled.
const readLoopMs = (stage: HTMLDivElement | null) => {
  const animations = stage?.getAnimations?.() ?? []
  const currentTime = animations[0]?.currentTime

  if (typeof currentTime !== 'number') {
    return null
  }

  return currentTime % HERO_LOOP_MS
}

const isSameState = (a: HeroTextState, b: HeroTextState) =>
  a.typedCount === b.typedCount &&
  a.isTyping === b.isTyping &&
  a.total === b.total

const useHeroClock = ({ stageRef, enabled, codeLength }: Args) => {
  const [state, setState] = useState<HeroTextState>(() =>
    heroTextStateAt(0, codeLength)
  )
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) {
      return
    }

    const tick = () => {
      const loopMs = readLoopMs(stageRef.current)

      if (loopMs !== null) {
        const next = heroTextStateAt(loopMs, codeLength)
        setState(prev => (isSameState(prev, next) ? prev : next))
      }

      frameRef.current = requestAnimationFrame(tick)
    }

    frameRef.current = requestAnimationFrame(tick)

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [enabled, codeLength, stageRef])

  return state
}

export default useHeroClock
